"use client"

import * as Accordion from "@radix-ui/react-accordion"
import { API_BASE_URL } from "@lib/config"
import { useMemo, useState } from "react"
import Image from "next/image"
// ── Types ──────────────────────────────────────────────────────────────────

type SellerApplicationForm = {
  brandName: string
  contactName: string
  email: string
  whatsapp: string
  country: string
  instagram: string
  website: string
  mainCategory: string
  priceRange: string
  catalogUrl: string
  pitch: string
}

type MarketplacePlan = {
  name: string
  price: string
  description: string
  recommended?: boolean
  features: string[]
}

type MarketingAddon = {
  name: string
  price: string
  basePrice: string
  investment: string
  description: string
  features: string[]
}

const initialFormData: SellerApplicationForm = {
  brandName: "",
  contactName: "",
  email: "",
  whatsapp: "",
  country: "",
  instagram: "",
  website: "",
  mainCategory: "",
  priceRange: "",
  catalogUrl: "",
  pitch: "",
}

// ── Static data ────────────────────────────────────────────────────────────

const trustItems = [
  "Curated marketplace",
  "One cart, many brands",
  "App + Web",
  "Global reach",
]

const pillars = [
  {
    number: "01",
    title: "Llegá más lejos",
    body: "Expandí tu alcance sin abrir nuevos locales ni multiplicar tu operación. Lealti te acerca a nuevas audiencias dentro y fuera de tu mercado.",
  },
  {
    number: "02",
    title: "Vendé con menos fricción",
    body: "Tus productos conviven con otras marcas y el cliente puede cerrar todo en una sola compra. Menos fricción en el checkout, más oportunidad de conversión.",
  },
  {
    number: "03",
    title: "Crecé sin perder marca",
    body: "Más exposición no debería significar menos identidad. En Lealti, tu universo visual, tu posicionamiento y tu propuesta siguen siendo protagonistas.",
  },
]

const platformFeatures = [
  {
    title: "Curaduría que suma valor",
    body: "Tu marca convive con propuestas seleccionadas, no con un mix genérico. Eso mejora percepción, contexto y afinidad.",
  },
  {
    title: "Descubrimiento real",
    body: "El cliente no llega solo por nombre de marca. También descubre por categoría, estilo, edit, temporada y universo visual.",
  },
  {
    title: "Web + App",
    body: "Tu marca aparece donde la compra sucede. Más puntos de contacto, más visibilidad, más oportunidad de venta.",
  },
  {
    title: "One cart, more conversion",
    body: "La compra multibrand en un solo checkout hace que tu producto compita mejor dentro de una experiencia más simple.",
  },
  {
    title: "Presentación premium",
    body: "Ficha, imagen, categoría, narrativa y recorrido: todo importa. Lealti pone el producto en un entorno más aspiracional y mejor resuelto.",
  },
  {
    title: "Operación más clara",
    body: "Onboarding, catálogo, seguimiento y postventa dentro de una lógica pensada para acompañar a las marcas, no para complicarlas.",
  },
]

const brandTiers = [
  {
    label: "Emerging brands",
    body: "Para marcas con identidad clara que necesitan una vidriera más potente.",
  },
  {
    label: "Growth brands",
    body: "Para marcas que ya venden y quieren escalar alcance sin perder posicionamiento.",
  },
  {
    label: "Established brands",
    body: "Para marcas que buscan un canal complementario con mejor contexto comercial y editorial.",
  },
]

const onboardingSteps = [
  {
    number: "01",
    title: "Aplicás",
    body: "Nos compartís tu marca, categorías, universo visual y catálogo.",
  },
  {
    number: "02",
    title: "Curamos",
    body: "Revisamos fit, propuesta, posicionamiento y potencial dentro del ecosistema Lealti.",
  },
  {
    number: "03",
    title: "Onboard",
    body: "Te guiamos en el alta y dejamos tu marca lista para vender.",
  },
  {
    number: "04",
    title: "Vendés y escalás",
    body: "Tu marca empieza a vivir en Lealti, con visibilidad, contexto de compra y espacio para crecer.",
  },
]

const marketplacePlans: MarketplacePlan[] = [
  {
    name: "Basic",
    price: "US$ 0,00",
    description:
      "Para empezar sin costo y publicar en el marketplace con comisión estándar.",
    features: [
      "Comisión: 12%",
      "AI imágenes: 0/mes",
      "AI textos: 0/mes",
      "Publicación en marketplace",
      "Comisión del 12% por venta",
    ],
  },
  {
    name: "Starter",
    price: "US$ 19,99",
    description:
      "Impulso inicial con AI para catálogo y SEO en un plan mensual accesible.",
    recommended: true,
    features: [
      "Comisión: 8%",
      "AI imágenes: 120/mes",
      "AI textos: 500/mes",
      "120 imágenes de catálogo con AI por mes",
      "500 generaciones de nombre + descripción con AI",
    ],
  },
  {
    name: "Growth",
    price: "US$ 49,99",
    description: "Más alcance y más contenido para marcas en expansión.",
    features: [
      "Comisión: 6%",
      "AI imágenes: 240/mes",
      "AI textos: 1000/mes",
      "240 imágenes de catálogo con AI por mes",
      "1000 generaciones de nombre + descripción con AI",
    ],
  },
  {
    name: "Pro",
    price: "US$ 199,99",
    description:
      "Escala avanzada para marcas con operación y volumen sostenido.",
    features: [
      "Comisión: 5%",
      "AI imágenes: 1000/mes",
      "AI textos: 5000/mes",
      "1000 imágenes de catálogo con AI por mes",
      "5000 generaciones de nombre + descripción con AI",
    ],
  },
  {
    name: "Enterprise",
    price: "US$ 1.999,99",
    description:
      "Solución premium para operación de alto volumen y máxima eficiencia.",
    features: [
      "Comisión: 3.5%",
      "AI imágenes: 10000/mes",
      "AI textos: 15000/mes",
      "10000 imágenes de catálogo con AI por mes",
      "15000 generaciones de nombre + descripción con AI",
    ],
  },
]

const marketingAddons: MarketingAddon[] = [
  {
    name: "Marketing Starter",
    price: "US$ 475,00",
    basePrice: "US$ 500,00",
    investment: "US$ 250,00",
    description:
      "Lealti crea y ejecuta campañas optimizadas, gestiona inversión y reporta resultados.",
    features: [
      "USD 250 de inversión publicitaria en Meta y Google Ads",
      "Campaña de email a base seleccionada por Lealti",
      "Reporte de inversión y rendimiento",
    ],
  },
  {
    name: "Marketing Growth",
    price: "US$ 950,00",
    basePrice: "US$ 1.000,00",
    investment: "US$ 600,00",
    description:
      "Estrategia con equipo dedicado para acelerar visibilidad y ventas.",
    features: [
      "USD 600 de inversión publicitaria en Meta y Google Ads",
      "Productos destacados en colecciones y categorías Lealti",
      "Equipo dedicado, asesoría y campañas de email a bases completas",
    ],
  },
  {
    name: "Marketing Pro",
    price: "US$ 2.660,00",
    basePrice: "US$ 2.800,00",
    investment: "US$ 2.000,00",
    description:
      "Máxima exposición en superficies clave de Lealti con performance continuo.",
    features: [
      "USD 2000 de inversión publicitaria en Meta y Google Ads",
      "Destacados en home, colecciones, categorías y resultados de búsqueda",
      "Equipo dedicado, asesoría, push móvil y campañas de email",
    ],
  },
]

const differentiators = [
  "Más visibilidad calificada",
  "Mejor contexto de compra",
  "Más oportunidades de cross-sell",
  "Una experiencia alineada con moda",
]

const faqItems = [
  {
    q: "¿Cualquier marca puede sumarse?",
    a: "No. Lealti funciona como un marketplace curado, por lo que cada marca pasa por una revisión previa.",
  },
  {
    q: "¿Necesito tener e-commerce propio?",
    a: "No necesariamente. Lo importante es tener una propuesta clara, catálogo y capacidad operativa alineada.",
  },
  {
    q: "¿Puedo vender fuera de mi país?",
    a: "Sí, según la operación y la configuración comercial/logística vigente de Lealti.",
  },
  {
    q: "¿Quién me acompaña en el alta?",
    a: "El equipo de Lealti te guía durante el onboarding para dejar tu marca lista para vender.",
  },
  {
    q: "¿Qué tipo de marcas funcionan mejor en Lealti?",
    a: "Las que tienen identidad, consistencia visual, producto fuerte y una propuesta clara dentro del universo moda/lifestyle.",
  },
]

// ── URL helper ─────────────────────────────────────────────────────────────

const resolveDashboardSignupUrl = () => {
  const directSignupUrl = process.env.NEXT_PUBLIC_DASHBOARD_SIGNUP_URL?.trim()
  if (directSignupUrl) return directSignupUrl

  const dashboardBaseUrl = process.env.NEXT_PUBLIC_DASHBOARD_URL?.trim()
  if (dashboardBaseUrl) {
    return `${dashboardBaseUrl.replace(/\/+$/, "")}/auth/signup`
  }

  return "https://dashboard.lealti.com/auth/signup"
}

// ── Shared sub-components ──────────────────────────────────────────────────

function Eyebrow({
  children,
  light = false,
}: {
  children: React.ReactNode
  light?: boolean
}) {
  return (
    <p
      className={`text-[10px] font-lealti uppercase tracking-[0.34em] ${
        light ? "text-white/40" : "text-black/40"
      }`}
    >
      {children}
    </p>
  )
}

function PrimaryButton({
  href,
  children,
  className = "",
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={`inline-flex items-center justify-center border border-black bg-black px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-white transition-colors duration-200 hover:bg-transparent hover:text-black ${className}`}
    >
      {children}
    </a>
  )
}

function OutlineButton({
  href,
  children,
  className = "",
}: {
  href: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <a
      href={href}
      className={`inline-flex items-center justify-center border border-black px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-black transition-colors duration-200 hover:bg-black hover:text-white ${className}`}
    >
      {children}
    </a>
  )
}

function EditorialPlaceholder({
  label,
  className = "",
}: {
  label: string
  className?: string
}) {
  return (
    <div
      className={`relative overflow-hidden bg-[#161616] ${className}`}
      aria-hidden="true"
    >
      <Image
        src="/sellers-6.jpg"
        alt={label}
        className="object-cover w-auto h-auto"
        sizes="100vw"
        fill
      />
    </div>
  )
}

// ── FormField ──────────────────────────────────────────────────────────────

function FormField({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  error,
  required = false,
}: {
  id: keyof SellerApplicationForm
  label: string
  type?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  error?: string
  required?: boolean
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[10px] font-lealti uppercase tracking-[0.24em] text-black/50"
      >
        {label}
        {required && <span className="ml-1 text-black/30">*</span>}
      </label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`mt-2.5 w-full border bg-[#fafafa] px-4 py-3 text-[14px] text-black outline-none transition-colors placeholder:text-black/20 focus:border-black focus:bg-white ${
          error ? "border-red-400" : "border-black/20"
        }`}
      />
      {error && <p className="mt-1.5 text-[11px] text-red-500">{error}</p>}
    </div>
  )
}

export default function SellersView() {
  const dashboardSignupUrl = useMemo(() => resolveDashboardSignupUrl(), [])

  const [formData, setFormData] =
    useState<SellerApplicationForm>(initialFormData)
  const [errors, setErrors] = useState<
    Partial<Record<keyof SellerApplicationForm, string>>
  >({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target

    setFormData((current) => ({
      ...current,
      [name]: value,
    }))

    if (errors[name as keyof SellerApplicationForm]) {
      setErrors((current) => ({
        ...current,
        [name]: "",
      }))
    }
  }

  const validateForm = () => {
    const nextErrors: Partial<Record<keyof SellerApplicationForm, string>> = {}

    if (!formData.brandName.trim()) nextErrors.brandName = "Campo obligatorio"
    if (!formData.contactName.trim())
      nextErrors.contactName = "Campo obligatorio"
    if (!formData.email.trim()) {
      nextErrors.email = "Campo obligatorio"
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nextErrors.email = "Email inválido"
    }
    if (!formData.whatsapp.trim()) nextErrors.whatsapp = "Campo obligatorio"
    if (!formData.country.trim()) nextErrors.country = "Campo obligatorio"
    if (!formData.mainCategory.trim())
      nextErrors.mainCategory = "Campo obligatorio"
    if (!formData.pitch.trim()) nextErrors.pitch = "Campo obligatorio"

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const submitWithPayload = async (payload: Record<string, string>) => {
    return fetch(`${API_BASE_URL}/public/sellers/onboarding`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    })
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)
    setSubmitError(null)

    const normalizedWebsite =
      formData.website.trim() || formData.instagram.trim()

    const completePayload = {
      name: formData.contactName.trim(),
      brandName: formData.brandName.trim(),
      email: formData.email.trim(),
      phone: formData.whatsapp.trim(),
      website: normalizedWebsite,
      country: formData.country.trim(),
      instagram: formData.instagram.trim(),
      mainCategory: formData.mainCategory.trim(),
      priceRange: formData.priceRange.trim(),
      catalogUrl: formData.catalogUrl.trim(),
      whyLealti: formData.pitch.trim(),
    }

    const fallbackPayload = {
      name: formData.contactName.trim(),
      brandName: formData.brandName.trim(),
      email: formData.email.trim(),
      phone: formData.whatsapp.trim(),
      website: normalizedWebsite,
    }

    try {
      let response = await submitWithPayload(completePayload)

      if (!response.ok) {
        response = await submitWithPayload(fallbackPayload)
      }

      if (!response.ok) {
        throw new Error("No se pudo enviar la postulación")
      }

      setIsSubmitted(true)
      setFormData(initialFormData)
    } catch {
      setSubmitError(
        "No pudimos enviar tu postulación. Intentá nuevamente en unos minutos."
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative bg-white text-black">
      <div className="pointer-events-none fixed bottom-5 right-5 z-50">
        <PrimaryButton
          href={dashboardSignupUrl}
          className="pointer-events-auto shadow-[0_8px_24px_rgba(0,0,0,0.22)]"
        >
          Comenzar a Vender
        </PrimaryButton>
      </div>

      {/* ── 1. HERO ──────────────────────────────────────────────────────── */}
      <section className="border-b border-black/10 px-6 pt-16 sm:px-10 lg:px-20">
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="grid items-stretch lg:grid-cols-2">
            <div className="flex flex-col justify-center py-16 lg:py-24 lg:pr-16">
              <h1 className="font-lealti-light text-[2.6rem] leading-[1.05] tracking-[-0.01em] sm:text-5xl lg:text-[4.25rem] lg:leading-[1.04]">
                <span className="block">Tu marca merece</span>
                <span className="block">más que un</span>
                <span className="block">marketplace.</span>
              </h1>
              <p className="mt-8 max-w-[520px] text-[17px] leading-relaxed text-black">
                Lealti conecta marcas de moda latinoamericana con una audiencia
                que entra a descubrir, guardar y comprar diseño. Más
                visibilidad, más contexto de marca y una experiencia premium en
                un ecosistema curado.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <PrimaryButton href={dashboardSignupUrl}>
                  Postulá tu marca
                </PrimaryButton>
                <OutlineButton href="mailto:marcas@lealti.com?subject=Hablar%20con%20el%20equipo%20Lealti">
                  Hablar con el equipo
                </OutlineButton>
                <PrimaryButton href={dashboardSignupUrl} className="md:hidden">
                  Comenzar a Vender
                </PrimaryButton>
              </div>
            </div>

            <EditorialPlaceholder
              label="Brand visual / campaign"
              className="min-h-[380px] lg:min-h-0"
            />
          </div>

          <div className="flex flex-wrap items-center border-t border-black/10 py-5">
            {trustItems.map((item, i) => (
              <div key={item} className="flex items-center">
                <span className="font-lealti text-[10px] uppercase tracking-[0.28em] text-black/45">
                  {item}
                </span>
                {i < trustItems.length - 1 && (
                  <span className="mx-5 select-none text-black/15">ー</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 2. MANIFESTO ──────────────────────────────────────────────── */}
      <section className="border-b border-black/10 px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <Eyebrow>Propuesta</Eyebrow>
          <h2 className="mt-7 max-w-[860px] font-lealti-light text-[2.2rem] leading-[1.1] sm:text-5xl lg:text-[3.5rem] lg:leading-[1.1]">
            No es un marketplace más.
          </h2>
          <div className="mt-10 max-w-[640px] space-y-5">
            <p className="text-[17px] leading-relaxed text-black">
              En Lealti tu marca no queda perdida en un catálogo infinito. Entra
              a un entorno pensado para moda, donde la curaduría, la estética y
              la experiencia de compra trabajan a favor de tu producto.
            </p>
            <p className="text-[17px] leading-relaxed text-black">
              Acá no se trata solo de publicar. Se trata de presentarte mejor,
              vender con más contexto y crecer sin diluir identidad.
            </p>
          </div>
          <div className="mt-12">
            <PrimaryButton href={dashboardSignupUrl}>
              Comenzar a Vender
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* ── 3. TRES PILARES ──────────────────────────────────────────── */}
      <section className="border-b border-black/10 bg-[#f9f9f9] px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="flex flex-wrap items-end justify-between gap-6 border-b border-black/10 pb-10">
            <div>
              <Eyebrow>Tres pilares</Eyebrow>
              <h2 className="mt-5 font-lealti-light text-3xl leading-tight sm:text-4xl">
                Para crecer sin compromisos.
              </h2>
            </div>
            <PrimaryButton
              href={dashboardSignupUrl}
              className="hidden sm:inline-flex"
            >
              Postulá tu marca
            </PrimaryButton>
          </div>

          <div className="mt-0 grid gap-0 md:grid-cols-3">
            {pillars.map((pillar, i) => (
              <article
                key={pillar.number}
                className={`py-12 ${
                  i !== 0
                    ? "border-t border-black/10 md:border-l md:border-t-0 md:pl-10"
                    : "md:pr-10"
                }`}
              >
                <p className="font-lealti text-[10px] uppercase tracking-[0.36em] text-black/30">
                  {pillar.number}
                </p>
                <h3 className="mt-5 font-lealti text-xl uppercase tracking-[0.07em]">
                  {pillar.title}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-black">
                  {pillar.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. PLATFORM ────────────────────────────────────────────────── */}
      <section className="border-b border-white/5 bg-[#111] px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <Eyebrow light>Plataforma</Eyebrow>
          <h2 className="mt-7 max-w-[760px] font-lealti-light text-[2.2rem] leading-[1.1] text-white sm:text-5xl lg:text-[3.25rem]">
            Built for fashion.{" "}
            <span className="text-white/40">Designed for growth.</span>
          </h2>
          <p className="mt-6 max-w-[580px] text-[17px] leading-relaxed text-white/45">
            Lealti está pensado para marcas que quieren crecer en un entorno
            alineado con diseño, deseo y experiencia.
          </p>

          <div className="mt-16 grid gap-px bg-white/[0.07] md:grid-cols-2 lg:grid-cols-3">
            {platformFeatures.map((feature) => (
              <article
                key={feature.title}
                className="bg-[#111] p-8 transition-colors duration-200 hover:bg-white/[0.04]"
              >
                <h3 className="font-lealti text-[11px] uppercase tracking-[0.18em] text-white/80">
                  {feature.title}
                </h3>
                <p className="mt-4 text-[14px] leading-relaxed text-white/45">
                  {feature.body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3">
            <a
              href={dashboardSignupUrl}
              className="inline-flex items-center justify-center border border-white bg-white px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-black transition-colors duration-200 hover:bg-transparent hover:text-white"
            >
              Postulá tu marca
            </a>
            <a
              href="mailto:marcas@lealti.com?subject=Consulta%20Lealti"
              className="inline-flex items-center justify-center border border-white/25 px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-white/55 transition-colors duration-200 hover:border-white hover:text-white"
            >
              Hablar con el equipo
            </a>
          </div>
        </div>
      </section>

      {/* ── 5. BRAND TIERS ─────────────────────────────────────────────── */}
      <section className="border-b border-black/10 px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <Eyebrow>Para marcas</Eyebrow>
          <h2 className="mt-7 max-w-[760px] font-lealti-light text-[2.2rem] leading-tight sm:text-5xl">
            Para marcas con ambición regional.
          </h2>
          <p className="mt-7 max-w-[700px] text-[17px] leading-relaxed text-black">
            Lealti está hecho para marcas latinoamericanas que quieren crecer
            con criterio. Funciona para marcas emergentes con producto fuerte,
            para marcas en expansión que quieren abrir un nuevo canal de venta,
            y para marcas con e-commerce propio que buscan sumar visibilidad y
            conversión en un entorno más curado.
          </p>

          <div className="mt-14 grid gap-8 md:grid-cols-3">
            {brandTiers.map((tier) => (
              <article
                key={tier.label}
                className="border-t-2 border-black pt-8"
              >
                <h3 className="font-lealti text-[11px] uppercase tracking-[0.2em]">
                  {tier.label}
                </h3>
                <p className="mt-5 text-[15px] leading-relaxed text-black">
                  {tier.body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-14">
            <PrimaryButton href={dashboardSignupUrl}>
              Comenzar a Vender
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* ── 6. ONBOARDING STEPS ───────────────────────────────────────── */}
      <section className="border-b border-black/10 bg-[#f9f9f9] px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <Eyebrow>Proceso</Eyebrow>
          <h2 className="mt-7 font-lealti-light text-3xl leading-tight sm:text-4xl">
            Cómo sumarte a Lealti
          </h2>

          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {onboardingSteps.map((step) => (
              <article
                key={step.number}
                className="border-t-2 border-black pt-8"
              >
                <p className="font-lealti text-[10px] uppercase tracking-[0.36em] text-black/30">
                  Paso {step.number}
                </p>
                <h3 className="mt-4 font-lealti text-lg uppercase tracking-[0.09em]">
                  {step.title}
                </h3>
                <p className="mt-4 text-[14px] leading-relaxed text-black">
                  {step.body}
                </p>
              </article>
            ))}
          </div>

          <div className="mt-14">
            <PrimaryButton href={dashboardSignupUrl}>
              Comenzar a Vender
            </PrimaryButton>
          </div>
        </div>
      </section>

      {/* ── 7. PLANS ──────────────────────────────────────────────────── */}
      <section className="border-b border-black/10 px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px] space-y-8">
          {/* Marketplace Plans */}
          <div className="rounded-2xl border border-black/[0.08] bg-[#f9f9f9] p-8 sm:p-10">
            <h2 className="font-lealti text-[18px] text-black">
              Planes marketplace
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-black/50">
              Incluye comisión por venta, cupos AI y facturación mensual en USD.
            </p>

            <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {marketplacePlans.map((plan) => (
                <div
                  key={plan.name}
                  className={`relative flex flex-col rounded-2xl bg-white p-6 ${
                    plan.recommended
                      ? "border-2 border-black/20 shadow-sm"
                      : "border border-black/[0.08]"
                  }`}
                >
                  {plan.recommended && (
                    <span className="absolute right-4 top-4 rounded-full border border-black/10 bg-white px-3 py-1 text-[9px] font-lealti uppercase tracking-[0.18em] text-black">
                      Recomendado
                    </span>
                  )}
                  <h3 className="font-lealti text-[15px] text-black">
                    {plan.name}
                  </h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-black/45">
                    {plan.description}
                  </p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-lealti-light text-[1.85rem] leading-none text-black">
                      {plan.price}
                    </span>
                    <span className="text-[12px] text-black/40">/mes</span>
                  </div>
                  <ul className="mt-5 flex-1 space-y-2">
                    {plan.features.map((f) => (
                      <li key={f} className="text-[13px] text-black/65">
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={dashboardSignupUrl}
                    className="mt-6 flex w-full items-center justify-center rounded-xl bg-[#5b54e8] px-4 py-3 text-[11px] font-lealti uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-[#4a44cc]"
                  >
                    Seleccionar
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Marketing Add-ons */}
          <div className="rounded-2xl border border-black/[0.08] bg-[#f9f9f9] p-8 sm:p-10">
            <h2 className="font-lealti text-[18px] text-black">
              Add-ons de marketing
            </h2>
            <p className="mt-1.5 text-[13px] leading-relaxed text-black/50">
              Lealti crea y ejecuta campañas optimizadas, gestiona inversión y
              reporta resultados.
            </p>

            <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {marketingAddons.map((addon) => (
                <div
                  key={addon.name}
                  className="flex flex-col rounded-2xl border border-black/[0.08] bg-white p-6"
                >
                  <h3 className="font-lealti text-[15px] text-black">
                    {addon.name}
                  </h3>
                  <p className="mt-1 text-[12px] leading-relaxed text-black/45">
                    {addon.description}
                  </p>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="font-lealti-light text-[1.85rem] leading-none text-black">
                      {addon.price}
                    </span>
                    <span className="text-[12px] text-black/40">/mes</span>
                  </div>
                  <p className="mt-1 text-[11px] text-black/35">
                    Precio base: {addon.basePrice} · descuento 5%
                  </p>
                  <ul className="mt-5 flex-1 space-y-2">
                    <li className="text-[13px] text-black/65">
                      Inversión incluida: {addon.investment}
                    </li>
                    {addon.features.map((f) => (
                      <li key={f} className="text-[13px] text-black/65">
                        {f}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={dashboardSignupUrl}
                    className="mt-6 flex w-full items-center justify-center rounded-xl bg-[#5b54e8] px-4 py-3 text-[11px] font-lealti uppercase tracking-[0.15em] text-white transition-colors duration-200 hover:bg-[#4a44cc]"
                  >
                    Seleccionar
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. DIFFERENTIATORS ───────────────────────────────────────── */}
      <section className="border-b border-black/10 bg-[#f9f9f9] px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="grid gap-14 lg:grid-cols-2 lg:gap-20 lg:items-start">
            <div>
              <Eyebrow>Diferencial</Eyebrow>
              <h2 className="mt-7 font-lealti-light text-[2.2rem] leading-tight sm:text-4xl lg:text-5xl">
                Más que ventas: posicionamiento.
              </h2>
              <p className="mt-7 text-[17px] leading-relaxed text-black">
                Lealti no solo te da un lugar para vender. Te da un contexto
                donde tu marca puede verse mejor, contar mejor su historia y
                formar parte de una selección con criterio.
              </p>
              <p className="mt-4 text-[17px] leading-relaxed text-black">
                Eso construye deseo, percepción y valor de marca a largo plazo.
              </p>
              <div className="mt-10">
                <PrimaryButton href={dashboardSignupUrl}>
                  Comenzar a Vender
                </PrimaryButton>
              </div>
            </div>

            <div className="divide-y divide-black/10">
              {differentiators.map((item, i) => (
                <div
                  key={item}
                  className="flex items-center justify-between py-5"
                >
                  <span className="font-lealti text-[13px] uppercase tracking-[0.15em]">
                    {item}
                  </span>
                  <span className="ml-6 flex-shrink-0 font-lealti text-[10px] uppercase tracking-[0.28em] text-black/25">
                    0{i + 1}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 9. FAQ ───────────────────────────────────────────────────────── */}
      <section className="border-b border-black/10 px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="grid gap-14 lg:grid-cols-[1fr_2fr] lg:gap-20">
            <div>
              <Eyebrow>FAQ</Eyebrow>
              <h2 className="mt-7 font-lealti-light text-3xl leading-tight sm:text-4xl">
                Preguntas frecuentes
              </h2>
            </div>

            <Accordion.Root
              type="multiple"
              className="divide-y divide-black/10 border-t border-black/10"
            >
              {faqItems.map((item) => (
                <Accordion.Item key={item.q} value={item.q}>
                  <Accordion.Trigger className="group flex w-full items-center justify-between py-6 text-left">
                    <span className="pr-4 font-lealti text-[12px] uppercase tracking-[0.12em]">
                      {item.q}
                    </span>
                    <span className="flex-shrink-0 font-lealti text-[22px] leading-none text-black/30 transition-transform duration-200 group-data-[state=open]:rotate-45">
                      +
                    </span>
                  </Accordion.Trigger>
                  <Accordion.Content className="data-[state=open]:animate-accordion-open data-[state=closed]:animate-accordion-close overflow-hidden">
                    <p className="pb-7 pr-10 text-[15px] leading-relaxed text-black">
                      {item.a}
                    </p>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </div>
        </div>
      </section>

      {/* ── 10. CTA SECTION ────────────────────────────────────────────── */}
      <section className="bg-[#111] px-6 py-24 sm:px-10 lg:px-20 lg:py-32">
        <div className="mx-auto w-full max-w-[1320px]">
          <Eyebrow light>Lealti para marcas</Eyebrow>
          <h2 className="mt-7 max-w-[700px] font-lealti-light text-[2.2rem] leading-[1.08] text-white sm:text-5xl lg:text-[3.5rem]">
            Sumá tu marca a Lealti.
          </h2>
          <p className="mt-7 max-w-[560px] text-[17px] leading-relaxed text-white/45">
            Si sentís que tu marca tiene lugar en un ecosistema curado,
            visualmente fuerte y comercialmente mejor resuelto, conversemos.
          </p>
          <div className="mt-12 flex flex-wrap gap-3">
            <a
              href={dashboardSignupUrl}
              className="inline-flex items-center justify-center border border-white bg-white px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-black transition-colors duration-200 hover:bg-transparent hover:text-white"
            >
              Comenzar a vender
            </a>
            <a
              href="mailto:marcas@lealti.com?subject=Hablar%20con%20el%20equipo%20Lealti"
              className="inline-flex items-center justify-center border border-white/25 px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-white/55 transition-colors duration-200 hover:border-white hover:text-white"
            >
              Hablar con el equipo
            </a>
          </div>
        </div>
      </section>

      {/* ── 11. APPLICATION FORM ────────────────────────────────── */}
      <section
        id="postulacion"
        className="border-t border-black/10 bg-[#f9f9f9] px-6 py-24 sm:px-10 lg:px-20 lg:py-32"
      >
        <div className="mx-auto w-full max-w-[1320px]">
          <div className="grid gap-16 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <Eyebrow>Postulación</Eyebrow>
              <h2 className="mt-7 font-lealti-light text-3xl leading-tight sm:text-4xl">
                Postulá tu marca
              </h2>
              <p className="mt-6 text-[17px] leading-relaxed text-black">
                Contanos sobre tu marca y te contactamos con los próximos pasos.
              </p>

              <div className="mt-12 divide-y divide-black/10">
                {[
                  {
                    label: "Curación previa",
                    desc: "Cada marca es revisada antes de entrar",
                  },
                  {
                    label: "Alcance regional",
                    desc: "Audiencia en Argentina, México y LATAM",
                  },
                  {
                    label: "Alta guiada",
                    desc: "El equipo te acompaña en cada paso",
                  },
                  {
                    label: "Integración simple",
                    desc: "Compatible con Shopify y e-commerce propio",
                  },
                ].map((item) => (
                  <div key={item.label} className="py-5">
                    <p className="font-lealti text-[11px] uppercase tracking-[0.24em]">
                      {item.label}
                    </p>
                    <p className="mt-1 text-[14px] text-black/50">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 sm:p-10 lg:p-12">
              {isSubmitted ? (
                <div className="space-y-5">
                  <Eyebrow>Postulación enviada</Eyebrow>
                  <h3 className="font-lealti-light text-3xl">
                    Gracias por aplicar
                  </h3>
                  <p className="text-[15px] leading-relaxed text-black">
                    Recibimos tu información y el equipo de Lealti la va a
                    revisar para contactarte con los próximos pasos.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsSubmitted(false)}
                    className="mt-2 inline-flex items-center justify-center border border-black bg-black px-7 py-3.5 text-[10px] font-lealti uppercase tracking-[0.28em] text-white transition-colors hover:bg-transparent hover:text-black"
                  >
                    Enviar otra postulación
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <FormField
                    id="brandName"
                    label="Nombre de la marca"
                    value={formData.brandName}
                    onChange={handleInputChange}
                    error={errors.brandName}
                    required
                  />
                  <FormField
                    id="contactName"
                    label="Nombre de contacto"
                    value={formData.contactName}
                    onChange={handleInputChange}
                    error={errors.contactName}
                    required
                  />

                  <div className="grid gap-6 sm:grid-cols-2">
                    <FormField
                      id="email"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={errors.email}
                      required
                    />
                    <FormField
                      id="whatsapp"
                      label="WhatsApp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      error={errors.whatsapp}
                      required
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <FormField
                      id="country"
                      label="País"
                      value={formData.country}
                      onChange={handleInputChange}
                      error={errors.country}
                      required
                    />
                    <FormField
                      id="instagram"
                      label="Instagram"
                      value={formData.instagram}
                      onChange={handleInputChange}
                      placeholder="@tumarca"
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <FormField
                      id="website"
                      label="Sitio web"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://"
                    />
                    <FormField
                      id="mainCategory"
                      label="Categoría principal"
                      value={formData.mainCategory}
                      onChange={handleInputChange}
                      error={errors.mainCategory}
                      required
                    />
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <FormField
                      id="priceRange"
                      label="Rango de precios"
                      value={formData.priceRange}
                      onChange={handleInputChange}
                      placeholder="Ej: USD 40–180"
                    />
                    <FormField
                      id="catalogUrl"
                      label="Link a catálogo / lookbook"
                      value={formData.catalogUrl}
                      onChange={handleInputChange}
                      placeholder="https://"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="pitch"
                      className="block text-[10px] font-lealti uppercase tracking-[0.24em] text-black/50"
                    >
                      Por qué tu marca debería estar en Lealti
                      <span className="ml-1 text-black/30">*</span>
                    </label>
                    <textarea
                      id="pitch"
                      name="pitch"
                      value={formData.pitch}
                      onChange={handleInputChange}
                      rows={5}
                      className={`mt-2.5 w-full border bg-[#fafafa] px-4 py-3 text-[14px] text-black outline-none transition-colors focus:border-black focus:bg-white ${
                        errors.pitch ? "border-red-400" : "border-black/20"
                      }`}
                    />
                    {errors.pitch && (
                      <p className="mt-1.5 text-[11px] text-red-500">
                        {errors.pitch}
                      </p>
                    )}
                  </div>

                  {submitError && (
                    <p className="border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-600">
                      {submitError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex w-full items-center justify-center border border-black bg-black px-6 py-4 text-[10px] font-lealti uppercase tracking-[0.3em] text-white transition-colors hover:bg-transparent hover:text-black disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSubmitting ? "Enviando..." : "Enviar postulación"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
